import { formatMoney } from "../../shared/pdp/utils/pricing";
import { getComparableShopifyId } from "../../shared/pdp/utils/shopifyIds";
import type {
  AddonQuantities,
  AddonSelection,
  SuggestedAddon,
  SuggestedAddonVariant,
} from "./types";

export type AddonTotals = {
  compareAtPrice: number;
  price: number;
};

const normalizeQuantity = (quantity: number) =>
  Number.isFinite(quantity) ? Math.max(0, Math.floor(quantity)) : 0;

export const getPreselectedQuantities = (
  addon: SuggestedAddon,
): AddonQuantities => {
  return (addon.preselectedVariants ?? []).reduce<AddonQuantities>(
    (quantities, selection) => {
      const variant = addon.variants.find(
        (candidate) =>
          getComparableShopifyId(candidate.variantId) ===
          getComparableShopifyId(selection.id),
      );
      const quantity = normalizeQuantity(selection.quantity);

      if (!variant || variant.isOutOfStock || quantity === 0) {
        return quantities;
      }

      quantities[variant.variantId] =
        (quantities[variant.variantId] ?? 0) + quantity;
      return quantities;
    },
    {},
  );
};

export const getDefaultQuantities = (
  addon: SuggestedAddon,
): AddonQuantities => {
  const defaults = getPreselectedQuantities(addon);

  if (Object.keys(defaults).length) return defaults;

  const smallestAvailableVariant = addon.variants
    .filter((variant) => !variant.isOutOfStock)
    .sort(
      (left, right) =>
        left.lawnSizeCovered - right.lawnSizeCovered ||
        left.weight.amount - right.weight.amount,
    )[0];
  return smallestAvailableVariant
    ? { [smallestAvailableVariant.variantId]: 1 }
    : {};
};

export const getProductQuantity = (quantities?: AddonQuantities) =>
  Object.values(quantities ?? {}).reduce(
    (total, quantity) => total + normalizeQuantity(quantity),
    0,
  );

export const getSelectionTotals = (
  addons: SuggestedAddon[],
  selection: AddonSelection,
): AddonTotals =>
  addons.reduce<AddonTotals>(
    (totals, addon) => {
      const quantities = selection[addon.productId] ?? {};

      addon.variants.forEach((variant) => {
        const quantity = normalizeQuantity(quantities[variant.variantId] ?? 0);
        totals.price += variant.priceInCents * quantity;
        totals.compareAtPrice +=
          (variant.compareAtPriceInCents ?? variant.priceInCents) * quantity;
      });

      return totals;
    },
    { compareAtPrice: 0, price: 0 },
  );

export const getAddonTotals = (
  addon: SuggestedAddon,
  quantities: AddonQuantities,
) => getSelectionTotals([addon], { [addon.productId]: quantities });

export const formatAmount = (amount: number) =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(amount);

export const getVariantLabel = (variant: SuggestedAddonVariant) =>
  `${formatAmount(variant.weight.amount)} ${variant.weight.unit} für ${new Intl.NumberFormat(
    "de-DE",
  ).format(variant.lawnSizeCovered)} m²`;

export const getVariantUnitPrice = (variant: SuggestedAddonVariant) => {
  const amountInBaseUnit =
    variant.weight.unit === "Kg"
      ? variant.weight.amount
      : variant.weight.amount / 1000;
  const displayUnit = variant.weight.unit === "ml" ? "l" : "kg";

  if (amountInBaseUnit <= 0) return null;
  return `${formatMoney(variant.priceInCents / amountInBaseUnit)}/${displayUnit}`;
};
